import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../i18n/LanguageContext';
import { Reveal, Kicker } from '../components/Reveal';
import { SEO } from '../components/SEO';
import { PhoneLink, EmailLink } from '../components/ProtectedContact';

const inputCls =
  'w-full bg-ivory-soft border border-emerald/15 rounded-sm px-4 py-3.5 text-sm text-emerald-ink placeholder:text-emerald-ink/40 outline-none transition-colors duration-300 focus:border-terracotta focus:ring-2 focus:ring-terracotta/30';

export default function Contatti() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', event_type: '', guests: '', message: '' });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          event_type: form.event_type,
          guests: form.guests ? parseInt(form.guests, 10) : null,
          message: form.message,
          language: lang,
        }),
      });
      if (!res.ok) throw new Error('bad status');
      toast.success(t.contatti.form.success);
      setForm({ name: '', email: '', phone: '', event_type: '', guests: '', message: '' });
    } catch {
      toast.error(t.contatti.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contatti-page">
      <SEO title={t.meta.contattiTitle} description={t.meta.contattiDesc} />

      <section className="bg-emerald-deep grain relative pt-40 pb-24 md:pb-32" data-testid="contatti-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <Kicker light>{t.contatti.kicker}</Kicker>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory tracking-tight max-w-3xl">{t.contatti.title}</h1>
            <p className="mt-6 max-w-2xl text-ivory/70 text-base md:text-lg leading-relaxed">{t.contatti.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>{t.contatti.infoTitle}</Kicker>
              <ul className="mt-10 space-y-8">
                <li className="flex items-start gap-5">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white border border-emerald/10 text-terracotta-dark shrink-0">
                    <Phone size={20} strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-emerald/50">{t.contatti.phoneLabel}</p>
                    <PhoneLink testid="contact-phone-link" className="mt-1 block font-serif text-2xl text-emerald hover:text-terracotta-dark transition-colors duration-300" />
                  </div>
                </li>
                <li className="flex items-start gap-5">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white border border-emerald/10 text-terracotta-dark shrink-0">
                    <Mail size={20} strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-emerald/50">{t.contatti.emailLabel}</p>
                    <EmailLink testid="contact-email-link" className="mt-1 block font-serif text-2xl text-emerald hover:text-terracotta-dark transition-colors duration-300" />
                  </div>
                </li>
                <li className="flex items-start gap-5">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white border border-emerald/10 text-terracotta-dark shrink-0">
                    <MapPin size={20} strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-emerald/50">{t.contatti.addressLabel}</p>
                    <p className="mt-1 font-serif text-2xl text-emerald" data-testid="contact-address">{t.contatti.address}</p>
                  </div>
                </li>
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-white border border-emerald/10 rounded-sm p-8 md:p-12 shadow-[0_8px_40px_rgba(37,79,87,0.08)]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="cf-name" className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">{t.contatti.form.name} *</label>
                    <input id="cf-name" data-testid="contact-input-name" required minLength={2} className={inputCls} value={form.name} onChange={set('name')} />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">{t.contatti.form.email} *</label>
                    <input id="cf-email" data-testid="contact-input-email" required type="email" className={inputCls} value={form.email} onChange={set('email')} />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">{t.contatti.form.phone}</label>
                    <input id="cf-phone" data-testid="contact-input-phone" type="tel" className={inputCls} value={form.phone} onChange={set('phone')} />
                  </div>
                  <div>
                    <label htmlFor="cf-guests" className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">{t.contatti.form.guests}</label>
                    <input id="cf-guests" data-testid="contact-input-guests" type="number" min={1} max={2000} className={inputCls} value={form.guests} onChange={set('guests')} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-type" className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">{t.contatti.form.eventType} *</label>
                    <select id="cf-type" data-testid="contact-select-event-type" required className={inputCls} value={form.event_type} onChange={set('event_type')}>
                      <option value="" disabled>—</option>
                      {t.contatti.form.types.map((ty) => (
                        <option key={ty} value={ty}>{ty}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-message" className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">{t.contatti.form.message} *</label>
                    <textarea id="cf-message" data-testid="contact-input-message" required minLength={5} rows={5} className={`${inputCls} resize-y`} value={form.message} onChange={set('message')} />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="mt-8 inline-flex items-center gap-3 rounded-full bg-emerald text-ivory px-10 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                >
                  {sending ? t.contatti.form.sending : t.contatti.form.submit} <Send size={15} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32" data-testid="contact-map">
        <Reveal>
          <div className="rounded-sm overflow-hidden border border-emerald/10 shadow-[0_8px_40px_rgba(37,79,87,0.10)]">
            <iframe
              title="Gustavo 28 — Via Gustavo Modena 28, Milano"
              src="https://maps.google.com/maps?q=Via%20Gustavo%20Modena%2028%2C%2020129%20Milano&z=16&output=embed"
              className="w-full h-[420px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
